import { createBrowserRouter, Navigate } from 'react-router-dom';
import { useRef } from 'react';
import { ProtectedAppLayout } from './protect/protected-app-layout';
import protect from './protect/protect';
import { PRODUCT } from '@/user/products/product.type';
import { PERMISSION } from '@/user/permissions/permission.type';
import { AuthPageLayout } from '@/auth/ui/auth-page-layout';
import { PageNotFound } from './page-not-found';
import { AppConfigProvider } from './app-config-provider';
import { userLoader } from '@/user/user-loader';
import { useQueryClient } from '@tanstack/react-query';
import { SignInPage } from '@/auth/sign-in.page';
import { SignUpInvitationExpiredPage } from '@/auth/sign-up-invitation-expired.page';
import { SignUpPage } from '@/auth/sign-up.page';
import { ExploreFormPage } from '@/features/explore/explore-form.page';
import { ExploreResultsPage } from '@/features/explore/explore-results.page';
import { RequestProjectPage } from '@/features/request-project/request-project.page';
import { RequestProjectWithLinkWrapperPage } from '@/features/request-project/request-project-with-link-wrapper.page';
import { RequestProjectSubmissionWithLinkWrapperPage } from '@/features/request-project/request-project-submission-with-link-wrapper.page';
import { MeetingWithLinkWrapperPage } from '@/features/meeting/meeting-with-link-wrapper.page';
import { CommentWithLinkWrapperPage } from '@/features/comment/comment-with-link-wrapper.page';
import { AccountWithLinkWrapperPage } from '@/features/account/account-with-link-wrapper.page';
import { AskToJoinWithLinkWrapperPage } from '@/features/ask-to-join/ask-to-join-with-link-wrapper.page';
import { HomePage } from '@/features/home/home.page';
import { McpOAuthCallback } from '@/features/settings/api-and-integrations/mcp-oauth/mcp-oauth-callback';
import { SettingsPage } from '@/features/settings/settings.page';
import { Companies } from './companies';
import { Projects } from './projects';
import { Teams } from './teams';
import { ErrorAppLayout } from './error-app-layout';
import { AccountDisabledPage } from './account-disabled.page';
import { FeedPage } from '@/features/feed/feed.page';
import { SpotlightCompanyPage } from '@/features/spotlight/spotlight-company.page';
import { SpotlightWithLinkWrapperPage } from '@/features/spotlight/spotlight-with-link-wrapper.page';

export const useRouter = () => {
  const queryClient = useQueryClient();
  const routerRef = useRef<ReturnType<typeof createBrowserRouter> | null>(null);

  if (!routerRef.current) {
    routerRef.current = createBrowserRouter([
      {
        element: <AppConfigProvider />,
        errorElement: <PageNotFound />,
        children: [
          {
            path: '/',
            element: <ProtectedAppLayout />,
            children: [
              { index: true, element: <Navigate to="home" /> },
              {
                path: 'home',
                element: <HomePage />,
                loader: userLoader(queryClient),
              },
              {
                path: 'companies/*',
                element: <Companies />,
              },
              {
                path: 'projects/*',
                element: <Projects />,
              },
              {
                path: 'teams/*',
                element: <Teams />,
              },
              {
                path: 'settings',
                element: <SettingsPage />,
              },
              {
                path: 'spotlight/companies/:companyId',
                element: protect(<SpotlightCompanyPage />, {
                  products: [PRODUCT.SPOTLIGHT],
                  permissions: [PERMISSION.VIEW_ORGANIZATION_HOME_SCREEN],
                }),
                loader: userLoader(queryClient),
              },
            ],
          },
          {
            path: 'request-project/:linkId',
            element: <RequestProjectWithLinkWrapperPage />,
          },
          {
            // Must stay after spotlight/companies/:companyId so "companies" is not a linkId.
            path: 'spotlight/:linkId',
            element: <SpotlightWithLinkWrapperPage />,
          },
          {
            element: <ProtectedAppLayout withTopbar={false} />,
            children: [
              {
                path: 'explore',
                element: protect(
                  <ExploreFormPage />,
                  {
                    permissions: [PERMISSION.ADD_COMPANY_TO_STREAM],
                    products: [PRODUCT.CRUNCHBASE, PRODUCT.EXPLORE],
                  },
                  {
                    navigateToNotFound: true,
                  },
                ),
              },
              {
                path: 'explore/results/:requestId?',
                element: protect(<ExploreResultsPage />, {
                  products: [PRODUCT.EXPLORE],
                }),
              },
              {
                path: 'request-project',
                element: <RequestProjectPage />,
              },
              {
                path: 'feed',
                element: <FeedPage />,
                loader: userLoader(queryClient),
              },
            ],
          },
          {
            path: 'request-project-submission/link/:linkId',
            element: <RequestProjectSubmissionWithLinkWrapperPage />,
          },
          {
            path: 'meetings/link/:linkId',
            element: <MeetingWithLinkWrapperPage />,
          },
          {
            path: 'comments/link/:linkId',
            element: <CommentWithLinkWrapperPage />,
          },
          {
            path: 'account/:accountId',
            element: <AccountWithLinkWrapperPage />,
          },
          {
            path: 'ask-to-join/link/:linkId',
            element: <AskToJoinWithLinkWrapperPage />,
          },
          {
            // OAuth return must not sit under ProtectedAppLayout — a full-page
            // redirect from Descope remounts the SPA; the auth guard would send
            // users to /auth/signin before the callback can exchange code/state.
            path: 'settings/mcp-oauth/callback',
            element: <McpOAuthCallback />,
          },
          {
            path: 'auth',
            element: <AuthPageLayout />,
            children: [
              {
                path: 'signin',
                element: <SignInPage />,
              },
              {
                path: 'signup',
                element: <SignUpPage />,
              },
              {
                path: 'invitation-expired',
                element: <SignUpInvitationExpiredPage />,
              },
            ],
          },
          {
            path: '404',
            element: <PageNotFound />,
          },
          {
            path: '/error',
            element: <ErrorAppLayout />,
            errorElement: <PageNotFound />,
            children: [
              {
                path: 'account-disabled',
                element: <AccountDisabledPage />,
              },
            ],
          },
        ],
      },
    ]);
  }

  return routerRef.current;
};
